import * as React from "react";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import { toISODateTimeString, lowHigh, capitalize, createDurationString } from "@/lib/util";

import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import * as ta from "@/lib/backend/alerts.generated.types";
import * as td from "@/lib/backend/datasources.generated.types";
import { EVENT_TYPES } from "@/lib/models";
import { createAkaUrl } from "@/lib/aka";

import ErrorComponent from "../ui/ErrorComponent";

type EventDataSource = { event: ta.IEventData; datasource: td.IDatasourceRec };
type EventError = { event: ta.IEventData; error: string };

export type SelectedEvent = EventDataSource | EventError;

type RenderResult = {
    type: string;
    name: string;
    ctrl: React.ReactNode;
};

type Props = {
    selected: SelectedEvent;
};

type State = {
    line_uuid: string | null;
    line_title: string | null;
    plant_title: string | null;
    error: string | null;
};

/**
 * Type guard to check if the selected event is an error
 */
function isEventError(selected: SelectedEvent): selected is EventError {
    return "error" in selected;
}

/**
 * Function that retrieves any additional data needed to display event
 * */
export async function getEventDetails(event: ta.IEventData, done: (arg: SelectedEvent) => void) {
    // first get datasource info
    try {
        const res = await getBackend2().datasources.getSingleDatasource({ id: event.datasource_uuid || "UNKNOWN" });
        const datasource = res.data;
        if (datasource) {
            done({ event: event, datasource });
        } else {
            done({ event: event, error: "Datasource not found." });
        }
    } catch (e) {
        done({ event: event, error: (e as Error).message });
    }
}

/**
 * Function that prepares input for the given event into event table
 * */
export function renderEventRow(event: ta.IEventData) {
    // placeholder for results
    const res = {
        type: "",
        extra_data: <span />,
    };
    // go over different event types
    if (event.type === EVENT_TYPES.man_downtime && event.version === 1) {
        const ed = event.extra_data;
        const tags = event.tags;
        let desc = "";
        if (tags.plant_title || tags.plant_external_id) {
            desc += " - " + (tags.plant_title || tags.plant_external_id);
        }
        if (tags.line_title || tags.line_external_id) {
            desc += " - " + (tags.line_title || tags.line_external_id);
        }

        const span = createDurationString(event.ts_to - event.ts);
        res.type = `Downtime`;
        res.extra_data = (
            <p className="data_source_text">
                Downtime - {span} - {ed.title} {desc}
            </p>
        );
    }
    return res;
}

/**
 * Function that prepares input for the given event into event list
 * */
export function renderEventListRow(event: ta.IEventData) {
    // placeholder for results
    let res = {
        type: "",
        desc: "",
        extra_data: <span />,
    };
    // go over different event types
    if (event.type === EVENT_TYPES.man_downtime && event.version === 1) {
        const tags = event.tags;
        res.desc = event.description || "";
        if (tags.plant_title || tags.plant_external_id) {
            res.desc += " - " + (tags.plant_title || tags.plant_external_id || "");
        }
        if (tags.line_title || tags.line_external_id) {
            res.desc += " - " + (tags.line_title || tags.line_external_id || "");
        }
    }
    res.type = event.title || event.type;
    res.desc = event.description || event.type + " event occured";

    res.extra_data = (
        <div>
            <span className="bold">{res.type} </span>
            <br />
            <span className="light">{res.desc} </span>
            <br />
            <Moment fromNow>{new Date(event.ts)}</Moment> &bull;
            <span className="light"> {toISODateTimeString(new Date(event.ts))}</span> &bull;
            <span> {event.analysis && event.analysis.params.type}</span>
        </div>
    );
    return res;
}

/**
 * Function that prepares input for the given event into event list
 * */
export function renderEventListRowRich(event: ta.IEventData) {
    // placeholder for results
    let res = {
        title: "",
        type: "",
        description: <span />,
        extra_data: <span />,
        icon: "",
    };
    let span: string | null = null;
    let show_to = false;
    if (event.ts !== event.ts_to) {
        span = createDurationString(event.ts_to - event.ts);
        show_to = true;
    }
    const datasource_title = event.datasource ? event.datasource.title : "UNKNOWN";

    res.title = capitalize(lowHigh(event.extra_data.extreme_type, "eng")) + " value of " + datasource_title;

    if (event.type === EVENT_TYPES.man_downtime && event.version === 1) {
        const ed = event.extra_data;
        res.type = "Downtime";
        res.title = "Reported downtime";
        res.description = (
            <p className="data_source_text">
                <span> Duration: {span} </span>
                <span> &bull; {ed.title} </span>
            </p>
        );
    }

    if (event.extra_data && event.extra_data.extreme_type === "high") {
        res.icon = "high";
    }
    if (event.extra_data && event.extra_data.extreme_type === "low") {
        res.icon = "low";
    }

    res.extra_data = (
        <div>
            <div className="row">
                <div className="col">
                    <h5 className={res.icon}>{res.title}</h5>
                    {res.description}
                </div>
            </div>
            <div className="date-time d-flex justify-content-between">
                <div className="bold">
                    {toISODateTimeString(new Date(event.ts))}
                    {show_to && " - " + toISODateTimeString(new Date(event.ts_to))}
                </div>
                <div className="bold">
                    <Moment fromNow>{new Date(event.ts)}</Moment>
                </div>
            </div>
        </div>
    );
    return res;
}

/**
 * Prepares a list of events to be displayed on event page as a scrollable side bar on the left
 * */
class EventDetails extends React.Component<Props, State> {
    state: State = {
        error: null,
        line_title: "-",
        line_uuid: "-",
        plant_title: "-",
    };

    async componentDidUpdate() {
        if (this.props.selected && this.props.selected.event.type === EVENT_TYPES.man_downtime) {
            const target = this.props.selected.event.tags.line_uuid;
            try {
                if (this.state.line_uuid !== target) {
                    const res = await getBackend2().manufacturing.getLines({ uuid: target });
                    if (res.lines.length === 0) {
                        throw new Error("Unknown line: " + target);
                    }
                    const line = res.lines[0];
                    this.setState({
                        line_title: line.title,
                        line_uuid: target,
                        plant_title: line.plant_title,
                    });
                }
            } catch (err) {
                this.setState({
                    line_title: "-",
                    line_uuid: target,
                    plant_title: "-",
                    error: (err as Error).message,
                });
            }
        }
    }

    renderManDowntime(event: ta.IEventData): RenderResult {
        let res: RenderResult = {
            type: event.type,
            name: "Manufacturing downtime",
            ctrl: <div />,
        };

        if (event.type === EVENT_TYPES.man_downtime && event.version === 1) {
            const ed = event.extra_data;
            const tg = event.tags;

            res.ctrl = (
                <div className="row">
                    <div className="col-lg-12 col-md-12">
                        <div className="white_box charts">
                            <div className="row">
                                <div className="col-lg-12 col-md-12">
                                    <h6> Detected manufacturing downtime "{ed.title}"</h6>
                                </div>
                                <div className="col-lg-6 col-md-6">
                                    <p className="data_source_text">
                                        <span>Duration:</span> {ed.duration} h{" "}
                                    </p>
                                    <p className="data_source_text">
                                        <span>Shift:</span> {ed.shift_id}{" "}
                                    </p>
                                    <p className="data_source_text">
                                        <span>Order:</span> {tg.order_external_id}{" "}
                                    </p>
                                    <p className="data_source_text">
                                        <span>User comment:</span> {ed.user_comment}{" "}
                                    </p>
                                </div>
                                <div className="col-lg-6 col-md-6">
                                    <p className="data_source_text">
                                        <span>Plant:</span> {this.state.plant_title}{" "}
                                    </p>
                                    <p className="data_source_text">
                                        <span>Line:</span> {this.state.line_title}{" "}
                                    </p>
                                    <p className="data_source_text">
                                        <span>Timestamp: </span> {toISODateTimeString(new Date(event.ts))}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return res;
    }

    renderExtraData() {
        if (isEventError(this.props.selected)) {
            return <ErrorComponent msg={this.props.selected.error} type="warning" />;
        }

        // placeholder for results
        let res: RenderResult = {
            type: "",
            name: "",
            ctrl: <div />,
        };

        const event = this.props.selected.event;
        const datasource = this.props.selected.datasource;
        // if there are tags present that can be used to redirect to another page, create a link
        let more_url: string | null = null;
        if (event.tags.module === "man" && event.tags.entity_type && event.tags.entity_external_id) {
            more_url = createAkaUrl({
                type: "man_" + event.tags.entity_type,
                external_id: event.tags.entity_external_id,
            });
        }
        if (event.type === EVENT_TYPES.man_downtime) {
            res = this.renderManDowntime(event);
        } else {
            res = {
                type: event.type,
                name: event.type,
                ctrl: <div />,
            };
        }

        let ts_section = (
            <div className="data_source_text">
                <span>Occured at: </span> {toISODateTimeString(new Date(event.ts))}
            </div>
        );
        if (event.ts !== event.ts_to) {
            ts_section = (
                <div className="data_source_text">
                    <span>Occured at: </span> {toISODateTimeString(new Date(event.ts))} <span> until </span>{" "}
                    {toISODateTimeString(new Date(event.ts_to))}
                </div>
            );
        }
        return (
            <div>
                <div className="row">
                    <div className="col-12">
                        <div className="active_data">
                            <h5>{res.name}</h5>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-12 col-md-12">
                        <div className="white_box charts">
                            <div className="row">
                                <div className="col-lg-6 col-md-6">
                                    <p className="data_source_text">
                                        <span>Type:</span> {res.type}{" "}
                                    </p>
                                    <p className="data_source_text">
                                        <span>Data source title:</span>{" "}
                                        <Link to={"/datasources/" + datasource.uuid}>{datasource.title}</Link>
                                    </p>
                                    <p className="data_source_text">
                                        <span>Data source code:</span>{" "}
                                        <Link to={"/datasources/" + datasource.uuid}>{datasource.code}</Link>
                                    </p>
                                </div>
                                <div className="col-lg-6 col-md-6">
                                    {ts_section}
                                    <p className="data_source_text">
                                        <span>Detected at:</span> {toISODateTimeString(new Date(event.ts_created))}{" "}
                                    </p>
                                    {more_url && (
                                        <p className="data_source_text">
                                            <Link to={more_url}>Show more...</Link>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {res.ctrl}
            </div>
        );
    }

    // prepare list of events
    render() {
        if (this.props.selected) {
            return (
                <div className="tab-content">
                    <div className="tab-pane active" id="home">
                        {this.renderExtraData()}
                    </div>
                </div>
            );
        }
        return <div />;
    }
}

export default EventDetails;
